import React from "react";
import { useTranslation } from "react-i18next";
import {
  Download,
  History,
  ListTree,
  Pencil,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import IconMegaphone from "@components/CustomIcons/IconMegaphone";
import ListItemCardCst from "@components/ListItemCardCst";
import {
  ListItemAction,
  ListItemBadge,
  ListItemCardSubtitleSegment,
} from "@components/ListItemCardCst/types";
import { mapEntityToCard } from "../../../Catalog/mapEntityToCard";
import { CatalogEntityType } from "../../../Catalog/types";
import { formatEnrollTypeLabel } from "../../../Catalog/UsersAndGroups/config/enrollTypeMap";
import { formatEnrollmentDate } from "../../../Catalog/UsersAndGroups/config/enrollmentDates";
import {
  enrollmentStatusVariant,
  resolveEnrollmentStatusKey,
} from "../../../Catalog/UsersAndGroups/config/enrollmentStatus";
import { buildExpirationBadge } from "../../../Catalog/UsersAndGroups/config/expirationBadge";
import { EnrollmentRowDisabledActions } from "../../../Catalog/UsersAndGroups/components/UserEnrollmentRow";

interface UserLoEnrollmentRowProps {
  entity: any;
  entityType: CatalogEntityType;
  onEditEnrollment?: (entity: any) => void;
  onDeleteEnrollment?: (entity: any) => void;
  onEditGroupEnrollment?: (entity: any) => void;
  onDeleteGroupEnrollment?: (entity: any) => void;
  onHistory?: (entity: any) => void;
  onDownloadCertificate?: (entity: any) => void;
  onSolicit?: (entity: any) => void;
  onViewProgress?: (entity: any) => void;
  // S05: azioni previste ma non consentite dai permessi, mostrate disabilitate.
  disabledActions?: EnrollmentRowDisabledActions;
}

const UserLoEnrollmentRow: React.FC<UserLoEnrollmentRowProps> = ({
  entity,
  entityType,
  onEditEnrollment,
  onDeleteEnrollment,
  onEditGroupEnrollment,
  onDeleteGroupEnrollment,
  onHistory,
  onDownloadCertificate,
  onSolicit,
  onViewProgress,
  disabledActions = {},
}) => {
  const { t } = useTranslation("catalog-v2");
  const { t: tActivity } = useTranslation("wizard-activity");
  const { t: tEnroll } = useTranslation("users-and-groups");

  const cardProps = mapEntityToCard(entity, entityType, {
    t,
    tActivity,
    onDetails: () => {}, //TODO implementare onDetails (pagina non ancora creata)
  });

  const statusKey = resolveEnrollmentStatusKey(entity);
  const expirationBadge = buildExpirationBadge(
    entity,
    String(tEnroll("card.expiration-badge.expired")),
  );

  const enrollmentBadges: ListItemBadge[] = [
    ...(entity.enrollType
      ? [{ label: formatEnrollTypeLabel(entity.enrollType) }]
      : []),
    {
      label: String(tEnroll(`card.status.${statusKey}`)),
      variant: enrollmentStatusVariant(statusKey),
    },
    ...(expirationBadge ? [expirationBadge] : []),
    ...(entity.mandatory
      ? [
          {
            variant:
              expirationBadge?.variant === "error"
                ? ("error" as const)
                : ("warning" as const),
            icon: <TriangleAlert size={14} />,
            label: "",
          },
        ]
      : []),
  ];

  const fromGroup = entity.massiveGroupId != null;

  const groupTypeLabel =
    entity.groupType === "DYNAMIC" || entity.groupType === "STATIC"
      ? String(
          tEnroll(
            entity.groupType === "DYNAMIC"
              ? "card.group-type.dynamic"
              : "card.group-type.static",
          ),
        )
      : null;

  const subtitleSegments: ListItemCardSubtitleSegment[] = [
    ...(cardProps.subtitle?.segments || []),
    ...(groupTypeLabel ? [{ label: groupTypeLabel }] : []),
  ];

  const solicitActions: ListItemAction[][] =
    onSolicit && statusKey !== "completed"
      ? [
          [
            {
              label: String(tEnroll("card.actions.solicit")),
              icon: <IconMegaphone size={14} />,
              onClick: () => onSolicit(entity),
              disabled: !!disabledActions.solicit,
            },
          ],
        ]
      : [];

  const progressActions: ListItemAction[][] = onViewProgress
    ? [
        [
          {
            label: String(tEnroll("action", { ns: "enrollment-progress" })),
            icon: <ListTree size={14} />,
            onClick: () => onViewProgress(entity),
            disabled: !!disabledActions.progress,
          },
        ],
      ]
    : [];

  const enrollmentActions: ListItemAction[][] =
    onDeleteEnrollment || onEditEnrollment
      ? [
          [
            {
              label: String(tEnroll("card.actions.edit-enrollment")),
              icon: <Pencil size={14} />,
              onClick: () => onEditEnrollment?.(entity),
              disabled: !onEditEnrollment || !!disabledActions.edit,
            },
            {
              label: String(tEnroll("card.actions.delete-enrollment")),
              icon: <Trash2 size={14} />,
              onClick: () => onDeleteEnrollment?.(entity),
              disabled:
                !onDeleteEnrollment ||
                statusKey === "completed" ||
                !!disabledActions.delete,
            },
          ],
          ...(fromGroup
            ? [
                [
                  {
                    label: String(
                      tEnroll("card.actions.edit-group-enrollment"),
                    ),
                    icon: <Pencil size={14} />,
                    onClick: () => onEditGroupEnrollment?.(entity),
                    disabled:
                      !onEditGroupEnrollment ||
                      !entity.groupType ||
                      !!disabledActions.editGroup,
                  },
                  {
                    label: String(
                      tEnroll("card.actions.delete-group-enrollment"),
                    ),
                    icon: <Trash2 size={14} />,
                    onClick: () => onDeleteGroupEnrollment?.(entity),
                    disabled:
                      !onDeleteGroupEnrollment ||
                      !entity.groupType ||
                      !!disabledActions.deleteGroup,
                  },
                ],
              ]
            : []),
        ]
      : [];

  // Storico e certificato hanno ciascuno il proprio permesso (passato dal
  // chiamante tramite il callback), non dipendono più da Elimina iscrizione.
  const historyActions: ListItemAction[][] = [
    ...(onHistory
      ? [
          [
            {
              label: String(tEnroll("card.actions.enrollment-history")),
              icon: <History size={14} />,
              onClick: () => onHistory(entity),
              disabled: !!disabledActions.history,
            },
          ],
        ]
      : []),
    ...(onDownloadCertificate
      ? [
          [
            {
              label: String(tEnroll("card.actions.download-certificate")),
              icon: <Download size={14} />,
              onClick: () => onDownloadCertificate(entity),
              disabled:
                !entity.certificateIds?.length || !!disabledActions.certificate,
            },
          ],
        ]
      : []),
  ];

  const moreActions: ListItemAction[][] = [
    ...enrollmentActions,
    ...solicitActions,
    ...progressActions,
    ...historyActions,
  ];

  return (
    <ListItemCardCst
      {...cardProps}
      subtitle={{
        ...cardProps.subtitle,
        groupId: fromGroup ? String(entity.massiveGroupId) : undefined,
        segments: subtitleSegments,
      }}
      details={{
        row1: `${tEnroll("card.completion-date")}: ${formatEnrollmentDate(
          entity.completeDate,
        )}`,
        row2: `${tEnroll("card.enrollment-date")}: ${formatEnrollmentDate(
          entity.enrollDate,
        )} | ${tEnroll("card.expiration-date")}: ${formatEnrollmentDate(
          entity.expirationDate,
        )}`,
      }}
      actions={[]}
      badges={[...(cardProps.badges || []), ...enrollmentBadges]}
      moreActions={moreActions}
    />
  );
};

export default UserLoEnrollmentRow;
